import { ArrowUpRight, FileText } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { FadeUp, Chapter } from "../components/Reveal";
import { ClosingCTA } from "../components/CTA";

const BRIEF_URL = "/assets/bos-executive-brief.pdf";

const CONTENTS = [
  "Recognise recurring execution signals",
  "Examine whether leadership-controlled operating conditions may be contributing",
  "Understand the five dimensions of Operational Clarity",
  "Distinguish what leaders believe is clear from what is usable in the work",
  "See how BOS Reveal makes the operating condition visible",
  "Identify the few conditions leaders can change",
];

export default function ExecutiveGuide() {
  return (
    <main data-testid="executive-guide-page">
      <PageHeader
        overline="BOS Executive Brief"
        titleLines={["When Capable People Still", "Struggle to Execute Consistently"]}
        lede="A practical brief for leaders examining whether unclear operating conditions are contributing to a recurring execution problem."
      />

      <section className="bg-paper border-t border-rule">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-32">
                <Chapter n="01">The brief</Chapter>
                <FadeUp className="mt-6">
                  <h2 className="font-display text-3xl md:text-[2.4rem] leading-tight tracking-tight">
                    A practical starting point for one recurring execution problem.
                  </h2>
                  <p className="mt-6 max-w-md text-base leading-relaxed text-mute">
                    The Brief connects recurring execution signals to the operating conditions
                    leaders can examine and change.
                  </p>
                  <p className="mt-6 max-w-md text-sm leading-relaxed text-mute">
                    Available on site. No sign-up required.
                  </p>
                </FadeUp>
              </div>
            </div>

            <div className="lg:col-span-7">
              <Chapter n="02">Contents</Chapter>
              <div className="mt-8">
                {CONTENTS.map((item, index) => (
                  <FadeUp key={item} delay={index * 0.04}>
                    <div
                      data-testid={`brief-chapter-${index + 1}`}
                      className="flex items-baseline gap-4 py-4 border-b border-rule"
                    >
                      <span className="text-[12px] font-semibold tracking-[0.18em] text-terra">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <p className="text-sm md:text-base text-ink/85">{item}</p>
                    </div>
                  </FadeUp>
                ))}
              </div>

              <FadeUp delay={0.2} className="mt-14 bg-ink text-paper px-6 md:px-10 py-10">
                <div className="flex items-start gap-5">
                  <FileText size={28} className="text-terra shrink-0 mt-1" />
                  <div>
                    <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-terra">
                      Executive Brief
                    </p>
                    <h3 className="mt-3 font-display text-2xl md:text-3xl tracking-tight">
                      When Capable People Still Struggle to Execute Consistently
                    </h3>
                    <p className="mt-3 max-w-lg text-sm leading-relaxed text-paper/60">
                      A practical brief for leaders examining whether unclear operating conditions
                      are contributing to a recurring execution problem.
                    </p>
                    <div className="mt-7">
                      <a
                        href={BRIEF_URL}
                        download
                        data-testid="brief-download"
                        className="inline-flex items-center gap-2 bg-terra text-white text-[12px] font-semibold uppercase tracking-[0.12em] px-6 py-3.5 hover:bg-paper hover:text-ink transition-colors duration-300"
                      >
                        Download the Executive Brief <ArrowUpRight size={15} />
                      </a>
                    </div>
                  </div>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <ClosingCTA
        title="Make one recurring execution problem visible."
        copy="BOS Reveal examines whether leadership-controlled operating conditions are contributing and identifies the few conditions leaders can change."
        primaryLabel="Explore BOS Reveal"
        primaryTo="/bos-reveal"
        showDiscussLink={false}
      />
    </main>
  );
}
